import { Link, router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'

// ─── Color policy ──────────────────────────────────────────────────────────────
// GREEN  = live / current / conditions met / completed successfully
// YELLOW = in-process / pending / approaching SLA threshold
// RED    = error / failed / SLA breached / cancelled
const C = { green: '#4CAF50', yellow: '#FF9800', red: '#F44336', blue: '#2196F3', gray: '#9CA3AF' }

// SLA thresholds per active milestone (minutes before yellow / red)
const SLA = {
  queue_activated:      { warn: 30,  crit: 60  },
  order_submitted:      { warn: 8,   crit: 15  },
  preparing:            { warn: 10,  crit: 20  },
  ready:                { warn: 5,   crit: 15  },
  guest_eta_update:     { warn: 20,  crit: 45  },
  guest_arrived:        { warn: 3,   crit: 8   },
  sensepoint_activated: { warn: 2,   crit: 5   },
  checkin_verified:     { warn: 2,   crit: 5   },
  guest_onboard:        { warn: 5,   crit: 15  },
  escrow_released:      { warn: 5,   crit: 15  },
  handoff_complete:     { warn: 3,   crit: 8   },
  delivered:            { warn: 5,   crit: 10  },
}

function slaColor(step, isoTimestamp) {
  const policy = SLA[step]
  if (!policy || !isoTimestamp) return C.green
  const min = (Date.now() - new Date(isoTimestamp).getTime()) / 60000
  if (min >= policy.crit) return C.red
  if (min >= policy.warn) return C.yellow
  return C.green
}

// Connection-health style freshness for En Route ETA updates
function etaFreshColor(isoTimestamp) {
  if (!isoTimestamp) return C.gray
  const sec = (Date.now() - new Date(isoTimestamp).getTime()) / 1000
  if (sec < 30)   return C.green
  if (sec < 180)  return C.yellow
  return C.red
}

function statusStyle(status) {
  const map = {
    active:                   { bg: '#E3F2FD', text: '#1565C0', border: '#90CAF9' },
    pending_creator_approval: { bg: '#FFF8E1', text: '#F57F17', border: '#FFE082' },
    paused:                   { bg: '#FFF3E0', text: '#E65100', border: '#FFCC80' },
    completed:                { bg: '#E8F5E9', text: '#2E7D32', border: '#A5D6A7' },
    cancelled:                { bg: '#FFEBEE', text: '#B71C1C', border: '#EF9A9A' },
    timeout:                  { bg: '#FFEBEE', text: '#B71C1C', border: '#EF9A9A' },
  }
  return map[status] || { bg: '#F5F5F5', text: '#616161', border: '#E0E0E0' }
}

// ─── Milestone config ──────────────────────────────────────────────────────────
const MILESTONE = {
  queue_activated:      { label: 'Activated',            color: '#2196F3' },
  // Order lifecycle milestones (Phase 1.5)
  order_submitted:      { label: 'Order In',             color: '#2196F3' }, // blue
  preparing:            { label: 'Preparing',            color: '#FF9800' }, // amber
  ready:                { label: 'Ready',                color: '#009688' }, // teal
  handoff_complete:     { label: 'Handoff Complete',     color: '#4CAF50' }, // green
  delivered:            { label: 'Delivered',            color: '#00C853' }, // emerald
  guest_eta_update:     { label: 'En Route',             color: '#2196F3' },
  guest_arrived:        { label: 'Arrived',              color: '#FFC107' },
  sensepoint_request:   { label: 'sensePoint Request',   color: '#FF9800' },
  sensepoint_ready:     { label: 'sensePoint Ready',     color: '#009688' },
  sensepoint_activated: { label: 'sensePoint Activated', color: '#009688' },
  sensepoint_rejected:  { label: 'sensePoint Rejected',  color: '#F44336' },
  checkin_verified:     { label: 'Check-in Verified',    color: '#009688' },
  guest_onboard:        { label: 'Guest Onboard',        color: '#00BCD4' },
  escrow_released:      { label: 'Escrow Released',      color: '#4CAF50' },
  session_closed:       { label: 'Session Closed',       color: '#607D8B' },
  mode_changed:         { label: 'Mode Changed',         color: '#607D8B' },
}

const LIFECYCLE_STEPS = [
  'queue_activated',
  'order_submitted',
  'preparing',
  'ready',
  'guest_eta_update',
  'guest_arrived',
  'sensepoint_request',    // guest taps Physical Check-in → yellow until creator confirms
  'sensepoint_activated',  // creator confirms → teal/green
  'checkin_verified',
  'guest_onboard',
  'escrow_released',
  'handoff_complete',
  'delivered',
  'session_closed',
]

function connectionHealth(lastSeenIso) {
  if (!lastSeenIso) return { label: 'Never seen', color: C.gray, status: 'unknown' }
  const gapSec = (Date.now() - new Date(lastSeenIso).getTime()) / 1000
  if (gapSec < 20)  return { label: `${Math.round(gapSec)}s ago`,      color: C.green,  status: 'Online' }
  if (gapSec < 180) return { label: `${Math.round(gapSec / 60)}m ago`, color: C.yellow, status: 'Recent' }
  if (gapSec < 300) return { label: `${Math.round(gapSec / 60)}m ago`, color: '#FF5722', status: 'Warning' }
  return               { label: `${Math.round(gapSec / 60)}m ago`,     color: C.red,    status: 'Offline' }
}

function getMilestone(key) {
  return MILESTONE[key] || { label: key?.replace(/_/g, ' ') || 'Unknown', color: C.gray }
}

function formatEta(seconds) {
  if (seconds == null) return null
  if (seconds === 0)   return 'Arrived'
  if (seconds < 60)    return `< 1 min`
  const m = Math.round(seconds / 60)
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const r = m % 60
  return r > 0 ? `${h}h ${r}m` : `${h}h`
}

function TimeAgo({ iso }) {
  if (!iso) return <span className="text-gray-400">—</span>
  const d = new Date(iso)
  return (
    <span title={d.toLocaleString()} className="text-gray-500 text-xs">
      {d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      {' · '}
      {d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
    </span>
  )
}

function SectionHeader({ children }) {
  return (
    <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 mt-6">
      {children}
    </h2>
  )
}

// ─── Collapse consecutive guest_eta_update events into groups ─────────────────
function groupEvents(events) {
  const groups = []
  let i = 0
  while (i < events.length) {
    if (events[i].event_type === 'guest_eta_update') {
      const etaEvents = [events[i]]
      while (i + 1 < events.length && events[i + 1].event_type === 'guest_eta_update') {
        i++
        etaEvents.push(events[i])
      }
      groups.push({ type: 'eta_group', events: etaEvents, id: `eta_${etaEvents[etaEvents.length - 1].id}` })
    } else {
      groups.push({ type: 'event', event: events[i] })
    }
    i++
  }
  return groups
}

// ─── ETA Group Row — collapsed by default for easy scanning ───────────────────
function EtaGroupRow({ group }) {
  const [expanded, setExpanded] = useState(false)
  const latest   = group.events[0]
  const oldest   = group.events[group.events.length - 1]
  const count    = group.events.length
  const etaSec   = latest.payload?.eta_seconds
  const etaLabel = formatEta(etaSec)
  const m        = getMilestone('guest_eta_update')
  const freshColor = etaFreshColor(latest.created_at)

  return (
    <li className="flex items-start gap-3">
      <div className="mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: freshColor }} />
      <div className="flex-1 min-w-0">
        <button onClick={() => setExpanded(v => !v)} className="w-full text-left">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-sm flex items-center gap-2" style={{ color: freshColor }}>
              {m.label}
              {count > 1 && (
                <span className="text-xs font-normal px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: freshColor + '20', color: freshColor }}>
                  ×{count}
                </span>
              )}
              {etaLabel && (
                <span className="text-xs font-normal" style={{ color: freshColor }}>
                  ETA: {etaLabel}
                </span>
              )}
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <TimeAgo iso={latest.created_at} />
              <span className="text-gray-300 text-xs">{expanded ? '▲' : '▼'}</span>
            </div>
          </div>
          <p className="text-xs text-gray-400 font-mono">guest_eta_update</p>
        </button>

        {expanded && (
          <ol className="mt-2 space-y-1.5 border-l-2 pl-3" style={{ borderColor: freshColor + '40' }}>
            {group.events.map((e, idx) => {
              const sec   = e.payload?.eta_seconds
              const label = formatEta(sec)
              return (
                <li key={e.id} className="text-xs text-gray-500">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono">
                      {label ?? `${sec}s`}
                      {idx === 0 && <span className="ml-1" style={{ color: freshColor }}>(latest)</span>}
                    </span>
                    <TimeAgo iso={e.created_at} />
                  </div>
                  {e.payload?.latitude != null && (
                    <p className="text-gray-400 font-mono">
                      {Number(e.payload.latitude).toFixed(5)}, {Number(e.payload.longitude).toFixed(5)}
                    </p>
                  )}
                </li>
              )
            })}
          </ol>
        )}
      </div>
      <span className="text-xs font-mono flex-shrink-0" style={{ color: C.gray }}>
        {count > 1 ? `#${oldest.event_seq}–#${latest.event_seq}` : `#${latest.event_seq}`}
      </span>
    </li>
  )
}

// ─── actor_role → badge label + colour ────────────────────────────────────────
const ACTOR_BADGE = {
  guest:   { label: 'Guest',   bg: '#E3F2FD', text: '#1565C0' },
  creator: { label: 'Creator', bg: '#FFF3E0', text: '#E65100' },
  system:  { label: 'System',  bg: '#F3E5F5', text: '#6A1B9A' },
  admin:   { label: 'Admin',   bg: '#FCE4EC', text: '#880E4F' },
}
function ActorBadge({ role }) {
  if (!role) return null
  const b = ACTOR_BADGE[role] || { label: role, bg: '#F5F5F5', text: '#616161' }
  return (
    <span className="text-xs font-semibold px-1.5 py-0.5 rounded"
      style={{ backgroundColor: b.bg, color: b.text }}>
      {b.label}
    </span>
  )
}

// ─── Single Event Row ─────────────────────────────────────────────────────────
function EventRow({ event: e, seq }) {
  const m = getMilestone(e.event_type)
  const actorRole = e.actor_role || e.payload?.actor_role
  return (
    <li className="flex items-start gap-3">
      <div className="mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: m.color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 font-semibold text-sm" style={{ color: m.color }}>
            {m.label}
            <ActorBadge role={actorRole} />
          </span>
          <TimeAgo iso={e.created_at} />
        </div>
        <p className="text-xs text-gray-400 font-mono">{e.event_type}</p>
        {e.payload && Object.keys(e.payload).length > 0 && (
          <details className="mt-1">
            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">Payload</summary>
            <pre className="text-xs bg-gray-50 rounded p-2 mt-1 overflow-x-auto text-gray-600">
              {JSON.stringify(e.payload, null, 2)}
            </pre>
          </details>
        )}
      </div>
      <span className="text-xs text-gray-300 font-mono flex-shrink-0">#{seq}</span>
    </li>
  )
}

const TERMINAL = ['completed', 'cancelled', 'timeout']

export default function QueueMonitorDetail({
  session: initialSession,
  events: initialEvents = [],
  messages: initialMessages = [],
  rails_env: railsEnv = 'production',
}) {
  const [session, setSession]   = useState(initialSession)
  const [events, setEvents]     = useState(initialEvents)
  const [messages, setMessages] = useState(initialMessages)
  const [lastRefresh, setLastRefresh] = useState(new Date())
  const [showCancelForm, setShowCancelForm] = useState(false)
  const [cancelReason, setCancelReason]     = useState('')
  const [forceCancelling, setForceCancelling] = useState(false)
  const cancelReasonRef = useRef(null)

  // Admin chat state
  const [adminChatText,      setAdminChatText]      = useState('')
  const [adminChatTarget,    setAdminChatTarget]     = useState('guest')   // 'guest' | 'creator' | 'both'
  const [adminChatSending,   setAdminChatSending]    = useState(false)
  const [adminChatError,     setAdminChatError]      = useState(null)
  const chatEndRef = useRef(null)

  const completedMilestones = new Set(events.map(e => e.event_type))

  const activeMilestone = LIFECYCLE_STEPS.reduce((found, step) => {
    return completedMilestones.has(step) ? step : found
  }, null)

  // Find event timestamp for active milestone (used for SLA coloring)
  const activeMilestoneEvent = events.find(e => e.event_type === activeMilestone)
  const activeBadgeColor = TERMINAL.includes(session.session_status)
    ? (session.session_status === 'completed' ? C.green : C.red)
    : slaColor(activeMilestone, activeMilestoneEvent?.created_at)

  const eventGroups = groupEvents([...events].reverse())

  async function handleDelete() {
    if (!window.confirm(`Delete session #${session.id}? This cannot be undone.`)) return
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    await fetch(`/admin/queue_monitor/${session.id}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrf, 'Accept': 'application/json' },
    })
    window.location.href = '/admin/queue_monitor'
  }

  async function handleAdminChat() {
    const note = adminChatText.trim()
    if (!note || adminChatSending) return
    setAdminChatSending(true)
    setAdminChatError(null)
    try {
      const res = await fetch(`/admin/queue_monitor/${session.id}/operator_message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
        },
        body: JSON.stringify({ message: note, target: adminChatTarget }),
      })
      if (!res.ok) throw new Error(`Server error ${res.status}`)
      setAdminChatText('')
      // Append optimistically so admin sees their message immediately
      const now = new Date().toISOString()
      const newMsgs = adminChatTarget === 'both'
        ? [
            { id: `opt-g-${now}`, message_type: 'operator_chat', sender_role: 'system', recipient_role: 'guest',   payload: { text: note }, created_at: now, delivery_status: 'pending' },
            { id: `opt-c-${now}`, message_type: 'operator_chat', sender_role: 'system', recipient_role: 'creator', payload: { text: note }, created_at: now, delivery_status: 'pending' },
          ]
        : [{ id: `opt-${now}`, message_type: 'operator_chat', sender_role: 'system', recipient_role: adminChatTarget, payload: { text: note }, created_at: now, delivery_status: 'pending' }]
      setMessages(prev => [...prev, ...newMsgs])
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    } catch (err) {
      setAdminChatError(err.message)
    } finally {
      setAdminChatSending(false)
    }
  }

  async function handleForceCancel() {
    if (!cancelReason.trim()) { cancelReasonRef.current?.focus(); return }
    if (!window.confirm(`Force-cancel session #${session.id}? This will notify both the guest and creator devices immediately.`)) return
    setForceCancelling(true)
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(`/admin/queue_monitor/${session.id}/force_cancel`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrf, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ reason: cancelReason.trim() }),
      })
      if (res.ok) {
        setShowCancelForm(false)
        setCancelReason('')
        router.reload({ only: ['session', 'events', 'messages'],
          onSuccess: (page) => {
            setSession(page.props.session)
            setEvents(page.props.events)
            setMessages(page.props.messages)
            setLastRefresh(new Date())
          },
        })
      } else {
        const body = await res.json().catch(() => ({}))
        window.alert(`Force cancel failed: ${body.error || res.statusText}`)
      }
    } finally {
      setForceCancelling(false)
    }
  }

  useEffect(() => {
    if (TERMINAL.includes(session.session_status)) return
    const interval = setInterval(() => {
      router.reload({
        only: ['session', 'events', 'messages'],
        onSuccess: (page) => {
          setSession(page.props.session)
          setEvents(page.props.events)
          setMessages(page.props.messages)
          setLastRefresh(new Date())
        },
      })
    }, 5000)
    return () => clearInterval(interval)
  }, [session.session_status])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <Link href="/offers" className="text-gray-400 hover:text-gray-700 text-sm">← Offers</Link>
            <span className="text-gray-300">/</span>
            <Link href="/admin/queue_monitor" className="text-gray-400 hover:text-gray-700 text-sm">Monitor</Link>
            <span className="text-gray-300">/</span>
            <h1 className="text-xl font-bold text-gray-900">
              Session #{session.id}
              <span className="ml-2 text-base font-normal text-gray-500">{session.offer_name}</span>
            </h1>
            {session.offer_id && (
              <Link href={`/offers/${session.offer_id}`}
                className="text-xs text-blue-600 hover:text-blue-800 border border-blue-200 px-2 py-0.5 rounded hover:bg-blue-50">
                View Offer →
              </Link>
            )}
            {railsEnv === 'development' && TERMINAL.includes(session.session_status) && (
              <button onClick={handleDelete}
                className="ml-auto text-xs text-red-500 border border-red-300 px-3 py-1 rounded hover:bg-red-50">
                Delete Session
              </button>
            )}
            {!TERMINAL.includes(session.session_status) && (
              <button onClick={() => setShowCancelForm(v => !v)}
                className="ml-auto text-xs text-orange-600 border border-orange-300 px-3 py-1 rounded hover:bg-orange-50">
                {showCancelForm ? 'Dismiss' : 'Force Cancel'}
              </button>
            )}
          </div>
          <div className="text-right">
            {TERMINAL.includes(session.session_status)
              ? <p className="text-xs text-gray-400">Monitoring paused · session closed</p>
              : <p className="text-xs text-gray-400">Live · refreshes 5s</p>
            }
            <p className="text-xs font-mono text-gray-500">{lastRefresh.toLocaleTimeString()}</p>
          </div>
        </div>
      </div>

      {session.session_status === 'paused' && (
        <div className="bg-orange-50 border-b border-orange-200 px-6 py-3">
          <div className="max-w-5xl mx-auto flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-sm font-semibold text-orange-800">
                ⏸ Session Paused — {session.paused_reason ? `${session.paused_reason} participant unreachable` : 'participant unreachable'}
              </p>
              <p className="text-xs text-orange-600 mt-0.5">
                Paused at <TimeAgo iso={session.paused_at} />
                {session.resume_deadline_at && (
                  <> · Auto-cancel deadline: <TimeAgo iso={session.resume_deadline_at} /></>
                )}
              </p>
            </div>
            <span className="text-xs text-orange-500 font-mono">Reconnection attempts in progress…</span>
          </div>
        </div>
      )}

      {showCancelForm && (
        <div className="bg-orange-50 border-b border-orange-200 px-6 py-4">
          <div className="max-w-5xl mx-auto">
            <p className="text-sm font-semibold text-orange-800 mb-2">Force Cancel — Session #{session.id}</p>
            <p className="text-xs text-orange-700 mb-3">
              This will immediately mark the session cancelled, broadcast a cancellation notice to both
              the guest and creator devices, and emit a <code className="font-mono bg-orange-100 px-1 rounded">session_closed</code> event.
            </p>
            <textarea ref={cancelReasonRef} value={cancelReason} onChange={e => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation (required — stored in session metadata for audit log)"
              rows={3}
              className="w-full text-sm border border-orange-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white resize-none" />
            <div className="flex gap-3 mt-2">
              <button onClick={handleForceCancel} disabled={forceCancelling || !cancelReason.trim()}
                className="text-sm font-semibold bg-orange-600 text-white px-4 py-1.5 rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {forceCancelling ? 'Cancelling…' : 'Confirm Force Cancel'}
              </button>
              <button onClick={() => { setShowCancelForm(false); setCancelReason('') }}
                className="text-sm text-orange-700 border border-orange-300 px-4 py-1.5 rounded hover:bg-orange-100">
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Color policy legend */}
      <div className="bg-white border-b border-gray-100 px-6 py-2">
        <div className="max-w-5xl mx-auto flex items-center gap-6 text-xs text-gray-500">
          <span className="font-semibold text-gray-400 uppercase tracking-wide">Live Data Policy:</span>
          {[
            { color: C.green,  label: 'Live / Conditions Met / Completed' },
            { color: C.yellow, label: 'In-Progress / Pending / Approaching SLA' },
            { color: C.red,    label: 'Error / SLA Breached / Cancelled' },
            { color: C.blue,   label: 'En Route / Informational' },
          ].map(({ color, label }) => (
            <span key={label} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
              {label}
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── Left: metadata + lifecycle ──────────────────────────────────── */}
        <div className="lg:col-span-1 space-y-4">

          {/* Session metadata card */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <SectionHeader>Session Info</SectionHeader>
            <dl className="space-y-2 text-sm">
              {(() => {
                const ss = statusStyle(session.session_status)
                return [
                  ['Status', (
                    <span key="status" style={{ backgroundColor: ss.bg, color: ss.text, border: `1px solid ${ss.border}` }}
                      className="px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide">
                      {session.session_status?.replace(/_/g, ' ')}
                    </span>
                  )],
                  ['SP Mode', <span key="mode" className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">{session.sensepoint_mode || 'cloud'}</span>],
                  ['Creator', session.creator_user_id ? `User #${session.creator_user_id}` : '—'],
                  ['Guest',   session.guest_user_id   ? `User #${session.guest_user_id}`   : '—'],
                  ['Offer',   <Link key="offer" href={`/offers/${session.offer_id}`} className="text-blue-500 hover:underline">#{session.offer_id} →</Link>],
                  ['Events',    session.event_count],
                  ['Messages',  session.message_count],
                  ['Started',   <TimeAgo key="started" iso={session.created_at} />],
                  ['Activated', <TimeAgo key="activated" iso={session.activated_at} />],
                  ['Closed',    <TimeAgo key="closed" iso={session.closed_at} />],
                ]
              })().map(([label, value]) => (
                <div key={label} className="flex justify-between items-start">
                  <dt className="text-gray-500 text-xs uppercase tracking-wide">{label}</dt>
                  <dd className="text-gray-800 text-right max-w-[55%]">{value}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Connection Health */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <SectionHeader>Connection Health</SectionHeader>
            {TERMINAL.includes(session.session_status) ? (
              <p className="text-xs text-gray-400 italic">Session closed — devices disconnected. Monitoring paused.</p>
            ) : (
              <div className="space-y-3">
                {[
                  {
                    role: session.sensepoint_mode === 'cloud' ? '☁️ SensePoint: cloud'
                        : session.sensepoint_mode === 'edge'  ? '⚡ SensePoint: edge'
                        : 'Creator (mobile SP)',
                    iso: session.creator_last_seen_at,
                    uid: session.creator_user_id,
                    virtual: ['cloud', 'edge'].includes(session.sensepoint_mode),
                  },
                  { role: 'Guest', iso: session.guest_last_seen_at, uid: session.guest_user_id, virtual: false },
                ].map(({ role, iso, uid, virtual }) => {
                  const h = virtual ? { label: 'Server-side', color: C.green, status: 'Virtual' } : connectionHealth(iso)
                  return (
                    <div key={role} className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-700">{role}</p>
                        <p className="text-xs text-gray-400 font-mono">{virtual ? 'no device' : uid ? `u${uid}` : '—'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: h.color }} />
                        <div className="text-right">
                          <p className="text-xs font-semibold" style={{ color: h.color }}>{h.label}</p>
                          <p className="text-xs text-gray-400">{h.status}</p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Lifecycle stepper */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <SectionHeader>Queue Lifecycle</SectionHeader>
            <ol className="relative space-y-0">
              {LIFECYCLE_STEPS.map((step, idx) => {
                const done   = completedMilestones.has(step)
                const active = step === activeMilestone
                const m      = getMilestone(step)
                const label  = m.label
                const isLast = idx === LIFECYCLE_STEPS.length - 1

                // Determine dot color
                let dotColor = '#D1D5DB' // pending gray
                if (active) {
                  dotColor = activeBadgeColor
                } else if (done) {
                  // Special condition overrides
                  if (step === 'guest_arrived') {
                    // Yellow until creator confirms sensepoint
                    dotColor = completedMilestones.has('sensepoint_activated') ? C.green : C.yellow
                  } else if (step === 'sensepoint_request') {
                    // Yellow (awaiting creator) until sensepoint_activated; then teal
                    dotColor = completedMilestones.has('sensepoint_activated')
                      ? MILESTONE.sensepoint_activated.color
                      : C.yellow
                  } else if (step === 'session_closed') {
                    dotColor = session.session_status === 'completed' ? C.green : C.red
                  } else {
                    dotColor = m.color
                  }
                }

                const labelColor = active ? activeBadgeColor
                  : done ? dotColor
                  : C.gray

                return (
                  <li key={step} className="flex items-start gap-3 pb-4 relative">
                    {!isLast && (
                      <div className="absolute left-[11px] top-5 bottom-0 w-0.5"
                        style={{ backgroundColor: done ? dotColor + '60' : '#E5E7EB' }} />
                    )}
                    <div className="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2 z-10"
                      style={{
                        backgroundColor: done || active ? dotColor : '#F9FAFB',
                        borderColor:     dotColor,
                        boxShadow:       active ? `0 0 0 3px ${dotColor}40` : undefined,
                      }}>
                      {done && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="pt-0.5">
                      <p className="text-sm font-medium" style={{ color: labelColor }}>
                        {label}
                        {active && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal">
                            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: activeBadgeColor }} />
                            <span style={{ color: activeBadgeColor }}>Current</span>
                          </span>
                        )}
                      </p>
                    </div>
                  </li>
                )
              })}
            </ol>
          </div>
        </div>

        {/* ── Right: events + messages ─────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-6">

          {/* Event Feed */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <SectionHeader>Event Feed ({events.length})</SectionHeader>
              <span className="text-xs text-gray-400 mb-3">newest first</span>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: '400px', minHeight: '120px' }}>
              {events.length === 0 ? (
                <p className="text-gray-400 text-sm">No events recorded yet.</p>
              ) : (
                <ol className="space-y-2 pr-1">
                  {eventGroups.map((group) =>
                    group.type === 'eta_group'
                      ? <EtaGroupRow key={group.id} group={group} />
                      : <EventRow   key={group.event.id} event={group.event} seq={group.event.event_seq} />
                  )}
                </ol>
              )}
            </div>
          </div>

          {/* ── Order Panel ────────────────────────────────────────────── */}
          {session.order_transaction && (() => {
            const ot = session.order_transaction
            const lineItems = ot.metadata?.line_items ?? []
            const totalMoney = ot.metadata?.total_money
            const fmtMoney = (amount, currency) => {
              if (amount == null) return '—'
              return (currency?.toLowerCase() === 'vuu') ? `⬡ ${(amount/100).toFixed(2)}` : `$${(amount/100).toFixed(2)}`
            }
            return (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <SectionHeader>Order #{ot.id}</SectionHeader>

                {/* Guest note amber banner */}
                {ot.guest_note && (
                  <div className="mb-3 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                    <span className="text-amber-500 text-sm">⚠</span>
                    <p className="text-xs text-amber-800 font-medium">
                      Guest note: &ldquo;{ot.guest_note}&rdquo;
                    </p>
                  </div>
                )}

                {/* Line items */}
                {lineItems.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Line Items</p>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-gray-400 border-b border-gray-100">
                          <th className="text-left pb-1 font-medium">Item</th>
                          <th className="text-center pb-1 font-medium w-8">Qty</th>
                          <th className="text-right pb-1 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((li, i) => (
                          <tr key={i} className="border-b border-gray-50">
                            <td className="py-1 text-gray-800">{li.name}</td>
                            <td className="py-1 text-center text-gray-500">{li.quantity ?? 1}</td>
                            <td className="py-1 text-right font-mono text-gray-800">
                              {fmtMoney(li.total_money?.amount ?? li.base_price_money?.amount, li.total_money?.currency ?? ot.settlement_currency)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {totalMoney && (
                        <tfoot>
                          <tr className="border-t border-gray-200">
                            <td colSpan={2} className="pt-1 text-gray-500 font-medium">Total</td>
                            <td className="pt-1 text-right font-mono font-bold text-gray-900">
                              {fmtMoney(totalMoney.amount, totalMoney.currency)}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )}

                {/* Order metadata */}
                <dl className="space-y-2 text-sm">
                  {[
                    ['Provider',          ot.provider],
                    ['Provider Order ID', ot.provider_order_id || '—'],
                    ['Order Status',      ot.status],
                    ['Fulfillment Type',  ot.fulfillment_type?.toUpperCase() || '—'],
                    ['Fulfillment State', ot.fulfillment_state || 'PROPOSED'],
                    ['Preparer Type',     ot.preparer_type || '—'],
                    ['Currency',          ot.settlement_currency?.toUpperCase()],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between items-start gap-2">
                      <dt className="text-gray-500 text-xs uppercase tracking-wide flex-shrink-0">{label}</dt>
                      <dd className="text-gray-800 text-right text-xs font-mono">{value}</dd>
                    </div>
                  ))}
                </dl>

                {/* Raw fulfillment JSON */}
                {ot.fulfillment_details && Object.keys(ot.fulfillment_details).length > 0 && (
                  <details className="mt-3">
                    <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                      Raw fulfillment JSON (Square-compatible)
                    </summary>
                    <pre className="text-xs bg-gray-50 rounded border border-gray-200 p-2 mt-1 overflow-x-auto text-gray-600">
                      {JSON.stringify(ot.fulfillment_details, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )
          })()}

          {/* ── Fulfillment Panel ───────────────────────────────────────── */}
          {session.order_transaction && (() => {
            const FSTATES = ['PROPOSED', 'RESERVED', 'PREPARING', 'READY', 'COMPLETED', 'CANCELLED']
            const currentState = session.order_transaction.fulfillment_state || 'PROPOSED'
            const currentIdx   = FSTATES.indexOf(currentState)

            const stateColors = {
              PROPOSED:  '#9E9E9E',
              RESERVED:  '#2196F3',
              PREPARING: '#FF9800',
              READY:     '#4CAF50',
              COMPLETED: '#1A237E',
              CANCELLED: '#F44336',
            }

            const handleForceState = (newState) => {
              if (!confirm(`Force fulfillment state to ${newState}?`)) return
              fetch(`/admin/queue_monitor/${session.id}/force_fulfillment_state`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
                },
                body: JSON.stringify({ state: newState }),
              }).then(r => r.json()).then(() => window.location.reload())
            }

            return (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <SectionHeader>Fulfillment Controls</SectionHeader>
                <div className="flex flex-wrap gap-2 mb-4">
                  {FSTATES.map((state, idx) => {
                    const done    = idx < currentIdx
                    const active  = state === currentState
                    const color   = stateColors[state] || '#9E9E9E'
                    return (
                      <button
                        key={state}
                        onClick={() => handleForceState(state)}
                        disabled={active}
                        className="text-xs px-3 py-1.5 rounded-full border font-medium transition-all"
                        style={{
                          backgroundColor: active ? color : done ? color + '20' : '#F5F5F5',
                          borderColor:     color,
                          color:           active ? '#fff' : color,
                          cursor:          active ? 'default' : 'pointer',
                          opacity:         active ? 1 : 0.75,
                        }}
                      >
                        {done ? '✓ ' : active ? '▶ ' : ''}{state}
                      </button>
                    )
                  })}
                </div>
                <dl className="space-y-1 text-xs mb-4">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Preparer</dt>
                    <dd className="font-mono text-gray-800">{session.order_transaction.preparer_type || '—'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Fulfillment Type</dt>
                    <dd className="font-mono text-gray-800">{session.order_transaction.fulfillment_type?.toUpperCase() || '—'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">AVA Scheduling</dt>
                    <dd className="text-gray-400 italic">Not yet linked [Phase 2 stub]</dd>
                  </div>
                </dl>
              </div>
            )
          })()}

          {/* Escrow Lifecycle Stepper */}
          {(() => {
            const STEPS = [
              { key: 'pending',             label: 'Pending',          note: 'Session created; no funds committed' },
              { key: 'held',                label: 'Held',             note: 'Creator approved; mock escrow locked' },
              { key: 'attestation_pending', label: 'Attest',           note: 'Guest arrived; awaiting sensePoint VC' },
              { key: 'released',            label: 'Released',         note: 'Delivery confirmed; funds released to creator' },
            ]
            const COLORS = {
              pending:             '#9E9E9E',
              held:                '#FF9800',
              attestation_pending: '#2196F3',
              released:            '#4CAF50',
              voided:              '#F44336',
            }
            const es = session.escrow_status || 'pending'
            const stepIdx = STEPS.findIndex(s => s.key === es)
            const isVoided = es === 'voided'
            return (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <SectionHeader>Escrow Lifecycle (Phase 2A Mock)</SectionHeader>
                {isVoided ? (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
                    <span className="text-red-600 text-sm font-semibold">Voided</span>
                    <span className="text-gray-400 text-xs ml-1">— cancelled or declined before delivery</span>
                  </div>
                ) : (
                  <ol className="mt-3 space-y-2">
                    {STEPS.map((step, i) => {
                      const done    = i < stepIdx
                      const current = i === stepIdx
                      const color   = COLORS[step.key]
                      return (
                        <li key={step.key} className="flex items-start gap-3">
                          <span
                            style={{
                              backgroundColor: done || current ? color : '#E5E7EB',
                              borderColor:     done || current ? color : '#D1D5DB',
                            }}
                            className="flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5"
                          >
                            {done && <span className="text-white text-[9px] font-bold">✓</span>}
                            {current && <span style={{ backgroundColor: color }} className="w-2 h-2 rounded-full block" />}
                          </span>
                          <div>
                            <p style={{ color: done || current ? color : '#6B7280' }} className="text-xs font-semibold">
                              {step.label}
                              {current && <span className="ml-1 text-[10px] font-normal opacity-70">(current)</span>}
                            </p>
                            <p className="text-gray-400 text-[11px]">{step.note}</p>
                          </div>
                        </li>
                      )
                    })}
                  </ol>
                )}
                {session.smart_contract_ref?.terms_hash && (
                  <p className="mt-3 text-[11px] text-gray-400 font-mono break-all">
                    Terms hash: {session.smart_contract_ref.terms_hash}
                  </p>
                )}
              </div>
            )
          })()}

          {/* Regulatory & Disclosure */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <SectionHeader>Regulatory &amp; Disclosure</SectionHeader>
            <dl className="space-y-2 text-sm">
              {[
                ['sensePoint Mode',    <span key="sp" className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs capitalize">{session.sensepoint_mode || 'cloud'}</span>],
                ['Brand / Operator',  session.offer_brand || <span className="text-gray-400">—</span>],
                ['Offer Category',    session.offer_service_type
                  ? <span key="cat" className="capitalize">{session.offer_service_type.replace(/_/g, ' ')}</span>
                  : <span key="cat-none" className="text-gray-400">General</span>],
                ['Disclosure Policy', session.offer_disclosure_policy || <span className="text-gray-400">None (Phase 1)</span>],
                ['Regulatory Framework', session.offer_regulatory_framework || <span className="text-gray-400">None (Phase 1)</span>],
                ['Disclosure Method', 'None (Phase 1 — manual visual)'],
                ['Consent Anchor',    'DWN VC — Phase 2A'],
                ['Escrow Trigger',    'XRPL EscrowFinish — Phase 3'],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between items-start gap-2">
                  <dt className="text-gray-500 text-xs uppercase tracking-wide flex-shrink-0">{label}</dt>
                  <dd className="text-gray-800 text-right max-w-[60%] text-xs">{value}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* ── Prep Chat Thread (read-only + admin can interject) ─────── */}
          {(() => {
            const prepMsgs = messages.filter(m =>
              m.message_type === 'preparer_note' ||
              m.message_type === 'guest_prep_note' ||
              m.message_type === 'operator_chat'
            )
            const roleLabel = { creator: '👨‍🍳 Preparer', guest: '👤 Guest', system: '⚡ Admin' }
            const roleBg    = { creator: '#1E3A5F', guest: '#1F2937', system: '#451A03' }
            const roleText  = { creator: '#93C5FD', guest: '#D1D5DB', system: '#FCD34D' }
            return (
              <div className="bg-gray-900 rounded-xl border border-gray-700 p-4 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-blue-400">👨‍🍳 Prep Chat</h3>
                  <span className="text-xs text-gray-500">{prepMsgs.length} messages</span>
                </div>

                {/* Guest note stub */}
                {session.order_transaction?.guest_note && (
                  <div className="mb-2 px-3 py-2 rounded bg-gray-800 border border-gray-700">
                    <p className="text-xs text-blue-300">
                      📋 Guest note: &ldquo;{session.order_transaction.guest_note}&rdquo;
                    </p>
                  </div>
                )}

                {/* Messages */}
                <div className="overflow-y-auto space-y-2 mb-3" style={{ maxHeight: '200px' }}>
                  {prepMsgs.length === 0
                    ? <p className="text-xs text-gray-600 text-center py-4">No prep messages yet.</p>
                    : prepMsgs.map(msg => {
                        const isAdmin = msg.sender_role === 'system'
                        const bg   = roleBg[msg.sender_role]   || '#374151'
                        const text = roleText[msg.sender_role] || '#D1D5DB'
                        const label = roleLabel[msg.sender_role] || msg.sender_role
                        return (
                          <div key={msg.id}
                            className="rounded-lg px-3 py-2 max-w-[85%]"
                            style={{
                              backgroundColor: bg,
                              alignSelf: isAdmin ? 'center' : msg.sender_role === 'creator' ? 'flex-end' : 'flex-start',
                              border: isAdmin ? '1px solid #92400E' : 'none',
                            }}>
                            <p className="text-xs font-semibold mb-1" style={{ color: text }}>{label}</p>
                            <p className="text-xs" style={{ color: text }}>
                              {msg.payload?.note ?? msg.payload?.text ?? ''}
                            </p>
                          </div>
                        )
                      })
                  }
                  <div ref={chatEndRef} />
                </div>

                {/* Admin send row */}
                <div className="border-t border-gray-700 pt-3 space-y-2">
                  <div className="flex gap-2">
                    {['guest', 'creator', 'both'].map(t => (
                      <button key={t}
                        onClick={() => setAdminChatTarget(t)}
                        className="text-xs px-2 py-1 rounded border transition-colors"
                        style={{
                          backgroundColor: adminChatTarget === t ? '#1E3A5F' : 'transparent',
                          borderColor: adminChatTarget === t ? '#3B82F6' : '#374151',
                          color: adminChatTarget === t ? '#93C5FD' : '#6B7280',
                        }}>
                        → {t}
                      </button>
                    ))}
                    <span className="text-xs text-gray-600 self-center ml-1">(acting as preparer)</span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      className="flex-1 text-xs bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-200 placeholder-gray-600 focus:outline-none focus:border-blue-500"
                      placeholder="Send message as admin preparer…"
                      value={adminChatText}
                      onChange={e => setAdminChatText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAdminChat() } }}
                      disabled={adminChatSending}
                    />
                    <button
                      onClick={handleAdminChat}
                      disabled={adminChatSending || !adminChatText.trim()}
                      className="text-xs px-3 py-2 rounded font-semibold transition-opacity"
                      style={{ backgroundColor: '#E94560', color: '#fff', opacity: (adminChatSending || !adminChatText.trim()) ? 0.5 : 1 }}>
                      {adminChatSending ? '…' : 'Send'}
                    </button>
                  </div>
                  {adminChatError && (
                    <p className="text-xs text-red-400">{adminChatError}</p>
                  )}
                </div>
              </div>
            )
          })()}

          {/* Status Messages */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <SectionHeader>Status Messages ({messages.length})</SectionHeader>
              <span className="text-xs text-gray-400 mb-3">newest first</span>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: '360px', minHeight: '80px' }}>
              {messages.length === 0 ? (
                <p className="text-gray-400 text-sm">No status messages yet.</p>
              ) : (
                <ol className="space-y-3 pr-1">
                  {[...messages].reverse().map(msg => {
                    const roleColors = {
                      creator: { bg: '#E94560', label: 'Creator' },
                      guest:   { bg: C.blue,   label: 'Guest' },
                      system:  { bg: '#607D8B', label: 'System' },
                    }
                    const sender    = roleColors[msg.sender_role]    || { bg: C.gray, label: msg.sender_role }
                    const recipient = roleColors[msg.recipient_role] || { bg: C.gray, label: msg.recipient_role }
                    return (
                      <li key={msg.id} className="rounded-lg border border-gray-100 p-3 bg-gray-50">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <span className="text-white text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: sender.bg }}>
                              {sender.label}
                            </span>
                            <span className="text-gray-400 text-xs">→</span>
                            <span className="text-white text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: recipient.bg }}>
                              {recipient.label}
                            </span>
                            <span className="text-xs font-mono text-gray-600 bg-white border border-gray-200 px-1.5 py-0.5 rounded">
                              {msg.message_type}
                            </span>
                            {msg.delivery_status === 'delivered' && (
                              <span className="text-xs" style={{ color: C.green }}>✓ delivered</span>
                            )}
                            {msg.delivery_status === 'pending' && (
                              <span className="text-xs" style={{ color: C.yellow }}>⏳ pending</span>
                            )}
                          </div>
                          <TimeAgo iso={msg.created_at} />
                        </div>
                        {msg.payload && Object.keys(msg.payload).length > 0 && (
                          <details className="mt-2">
                            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">Payload</summary>
                            <pre className="text-xs bg-white rounded border border-gray-200 p-2 mt-1 overflow-x-auto text-gray-600">
                              {JSON.stringify(msg.payload, null, 2)}
                            </pre>
                          </details>
                        )}
                      </li>
                    )
                  })}
                </ol>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
